import type {
  ActiveMarkerConfig,
  AnimationMapping,
  AnimationSource,
  AttackContext,
  CharacterConfig,
  DamageContext,
  HitContext,
  SubmeshDef,
  TransitionConfig
} from '../characters';
import { composeLifecycleScripts } from '../lifecycle';
import type { CharacterLifecycleScript, CharacterLifecycleScriptRef } from '../lifecycle';
import type { AttackDef as CombatAttackDef } from '../../types/combat';

export type CharacterCallbacks = {
  onDamageReceived?: (context: DamageContext) => number;
  onAttackStart?: (context: AttackContext) => void;
  onAttackHit?: (context: HitContext) => void;
  onHitReaction?: (context: DamageContext) => string | undefined;
};

type KnownCharacterKeys = keyof CharacterConfig;

type EnforceKnownKeys<T> = keyof T extends KnownCharacterKeys ? T : never;

type LifecycleDefinition = CharacterLifecycleScriptRef | CharacterLifecycleScriptRef[] | CharacterLifecycleScript[];

type CharacterDefinitionOverrides = {
  lifecycle?: LifecycleDefinition;
};

export type CharacterDefinition<TCallbacks extends CharacterCallbacks = CharacterCallbacks> =
  EnforceKnownKeys<
    Omit<CharacterConfig, keyof CharacterCallbacks | 'lifecycle'> &
      CharacterDefinitionOverrides &
      TCallbacks
  >;

const clonePhaseTiming = (phases?: AnimationSource['phases']): AnimationSource['phases'] | undefined => {
  if (!phases) {
    return undefined;
  }
  return {
    windup: phases.windup ? { ...phases.windup } : undefined,
    active: phases.active ? { ...phases.active } : undefined,
    release: phases.release ? { ...phases.release } : undefined,
    recovery: phases.recovery ? { ...phases.recovery } : undefined
  };
};

const cloneAnimationSource = (source: AnimationSource): AnimationSource => ({
  ...source,
  mask: source.mask ? [...source.mask] : undefined,
  phases: clonePhaseTiming(source.phases),
  exitTime: source.exitTime ? { ...source.exitTime } : undefined
});

const cloneAnimations = (animations?: AnimationMapping): AnimationMapping | undefined => {
  if (!animations) {
    return undefined;
  }
  const result: AnimationMapping = {};
  for (const [phase, source] of Object.entries(animations)) {
    result[phase] = typeof source === 'string' ? source : cloneAnimationSource(source);
  }
  return result;
};

const cloneTransitions = (transitions?: TransitionConfig[]): TransitionConfig[] | undefined =>
  transitions ? transitions.map((transition) => ({ ...transition })) : undefined;

const cloneRenderFixes = (fixes?: CharacterConfig['renderFixes']): CharacterConfig['renderFixes'] | undefined =>
  fixes ? { ...fixes } : undefined;

const cloneModelAxes = (axes?: CharacterConfig['modelAxes']): CharacterConfig['modelAxes'] | undefined =>
  axes ? { ...axes, forward: { ...axes.forward }, up: axes.up ? { ...axes.up } : undefined } : undefined;

const clonePhysics = (physics?: CharacterConfig['physics']): CharacterConfig['physics'] | undefined =>
  physics ? { ...physics } : undefined;

const cloneCombatBindings = (bindings?: CharacterConfig['combatBindings']): CharacterConfig['combatBindings'] | undefined =>
  bindings ? { ...bindings } : undefined;

const cloneProjectile = (projectile: CombatAttackDef['projectile']): CombatAttackDef['projectile'] =>
  projectile ? { ...projectile } : undefined;

const cloneWeaponConfig = (weapon: CombatAttackDef['weaponConfig']): CombatAttackDef['weaponConfig'] =>
  weapon ? { ...weapon } : undefined;

const cloneAttack = (attack: CombatAttackDef): CombatAttackDef => ({
  ...attack,
  maskBones: attack.maskBones ? [...attack.maskBones] : undefined,
  events: attack.events.map((event) => ({ ...event, params: event.params ? { ...event.params } : undefined })),
  projectile: cloneProjectile(attack.projectile),
  weaponConfig: cloneWeaponConfig(attack.weaponConfig)
});

const cloneCombatAttacks = (attacks?: Record<string, CombatAttackDef>): Record<string, CombatAttackDef> | undefined => {
  if (!attacks) {
    return undefined;
  }
  const result: Record<string, CombatAttackDef> = {};
  for (const [id, attack] of Object.entries(attacks)) {
    result[id] = cloneAttack(attack);
  }
  return result;
};

const cloneSubmesh = (submesh: SubmeshDef): SubmeshDef => ({
  ...submesh,
  animationMap: submesh.animationMap ? { ...submesh.animationMap } : undefined,
  rotationOffset: submesh.rotationOffset ? { ...submesh.rotationOffset } : undefined,
  positionOffset: submesh.positionOffset ? { ...submesh.positionOffset } : undefined
});

const cloneSubmeshes = (submeshes?: SubmeshDef[]): SubmeshDef[] | undefined =>
  submeshes ? submeshes.map(cloneSubmesh) : undefined;

const cloneVec3 = (vec?: ActiveMarkerConfig['offset']): ActiveMarkerConfig['offset'] =>
  vec ? { ...vec } : undefined;

const cloneActiveMarker = (
  marker?: ActiveMarkerConfig
): ActiveMarkerConfig | undefined => {
  if (!marker) {
    return undefined;
  }
  return {
    offset: cloneVec3(marker.offset),
    rotation: cloneVec3(marker.rotation)
  };
};

const ensureRequiredFields = (definition: CharacterDefinition): void => {
  if (!definition.id) {
    throw new Error('Character definition missing required id');
  }
  if (!definition.name) {
    throw new Error(`Character definition ${definition.id} missing required name`);
  }
  if (!definition.assetPath) {
    throw new Error(`Character definition ${definition.id} missing required assetPath`);
  }
  if (typeof definition.scale !== 'number' || Number.isNaN(definition.scale)) {
    throw new Error(`Character definition ${definition.id} must provide numeric scale`);
  }
};

/**
 * Create an immutable CharacterConfig from a strongly typed definition.
 * Keeps callbacks optional while cloning mutable collections for safety.
 *
 * @example // Damage multiplier
 * const daniel = createCharacter({
 *   id: 'daniel',
 *   name: 'Daniel',
 *   assetPath: '/assets/daniel.glb',
 *   scale: 1,
 *   onDamageReceived: ({ baseDamage }) => baseDamage * 0.8
 * });
 *
 * @example // Conditional hit reaction
 * const lilly = createCharacter({
 *   id: 'lilly',
 *   name: 'Lilly',
 *   assetPath: '/assets/lilly.glb',
 *   scale: 1,
 *   onHitReaction: ({ baseDamage }) => (baseDamage < 10 ? 'dodge_counter' : undefined)
 * });
 *
 * @example // Attack chaining
 * const comboFighter = createCharacter({
 *   id: 'combo',
 *   name: 'Combo Fighter',
 *   assetPath: '/assets/combo.glb',
 *   scale: 1,
 *   onAttackHit: ({ attackerId, attack, damage }) => {
 *     if (attack.id === 'combo_1' && damage > 0) {
 *       queueFollowUp(attackerId, 'combo_2');
 *     }
 *   }
 * });
 */
export const createCharacter = <TCallbacks extends CharacterCallbacks = CharacterCallbacks>(
  definition: CharacterDefinition<TCallbacks>
): CharacterConfig => {
  ensureRequiredFields(definition);

  const lifecycleValue = definition.lifecycle;
  const lifecycleRefs: CharacterLifecycleScriptRef[] =
    lifecycleValue === undefined
      ? []
      : Array.isArray(lifecycleValue)
      ? lifecycleValue
      : [lifecycleValue];
  const lifecycleScripts: CharacterLifecycleScript[] =
    lifecycleRefs.length > 0 ? composeLifecycleScripts(...lifecycleRefs) : [];

  return {
    id: definition.id,
    name: definition.name,
    assetPath: definition.assetPath,
    scale: definition.scale,
    renderFixes: cloneRenderFixes(definition.renderFixes),
    modelAxes: cloneModelAxes(definition.modelAxes),
    rotationOffset: definition.rotationOffset,
    moveSpeed: definition.moveSpeed,
    physics: clonePhysics(definition.physics),
    health: definition.health ? { ...definition.health } : undefined,
    orientationMode: definition.orientationMode,
    turnMode: definition.turnMode,
    animations: cloneAnimations(definition.animations),
    transitions: cloneTransitions(definition.transitions),
    animationMap: definition.animationMap,
    combatAttacks: cloneCombatAttacks(definition.combatAttacks),
    hitReactions: definition.hitReactions
      ? {
          cooldownSeconds: definition.hitReactions.cooldownSeconds,
          reactions: definition.hitReactions.reactions.map((reaction) => ({ ...reaction })),
          death: definition.hitReactions.death ? { ...definition.hitReactions.death } : undefined
        }
      : undefined,
    combatBindings: cloneCombatBindings(definition.combatBindings),
    submeshes: cloneSubmeshes(definition.submeshes),
    lifecycle: lifecycleScripts.length ? lifecycleScripts : undefined,
    onDamageReceived: definition.onDamageReceived,
    onAttackStart: definition.onAttackStart,
    onAttackHit: definition.onAttackHit,
    onHitReaction: definition.onHitReaction,
    activeMarker: cloneActiveMarker(definition.activeMarker)
  };
};

/**
 * Example: Damage multiplier callback.
 *
 * ```ts
 * const daniel = createCharacter({
 *   ...,
 *   onDamageReceived: ({ baseDamage, attackerId }) =>
 *     attackerId === 'boss' ? baseDamage * 0.5 : baseDamage
 * });
 * ```
 */